import { useCallback, useEffect, useMemo, useState } from "react";

const detectDevice = () => {
  if (typeof window === "undefined") {
    return {
      deviceType: "desktop",
      browserName: "Browser",
      isIos: false,
      isAndroid: false,
      isStandalone: false,
    };
  }

  const ua = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  const maxTouchPoints = window.navigator.maxTouchPoints || 0;
  const isIpadOs = platform === "MacIntel" && maxTouchPoints > 1;
  const isIos = /iphone|ipad|ipod/i.test(ua) || isIpadOs;
  const isAndroid = /android/i.test(ua);
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true;

  let browserName = "Browser";
  if (/edg/i.test(ua)) browserName = "Microsoft Edge";
  else if (/chrome|crios/i.test(ua) && !/edg/i.test(ua)) browserName = "Chrome";
  else if (/safari/i.test(ua) && !/chrome|crios|android/i.test(ua)) browserName = "Safari";
  else if (/firefox|fxios/i.test(ua)) browserName = "Firefox";

  return {
    deviceType: isIos ? "ios" : isAndroid ? "android" : "desktop",
    browserName,
    isIos,
    isAndroid,
    isStandalone,
  };
};

export const usePwaInstall = () => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [device, setDevice] = useState(() => detectDevice());
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [installResult, setInstallResult] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const refreshDevice = () => setDevice(detectDevice());
    refreshDevice();

    const handlePrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
      setInstallResult("Install is available on this device.");
    };

    const handleInstalled = () => {
      setPromptEvent(null);
      setGuideOpen(false);
      setDevice(detectDevice());
      setInstallResult("ManuCalc is already installed.");
    };

    window.addEventListener("beforeinstallprompt", handlePrompt);
    window.addEventListener("appinstalled", handleInstalled);

    navigator.serviceWorker?.ready
      ?.then(() => setServiceWorkerReady(true))
      .catch(() => setServiceWorkerReady(false));

    return () => {
      window.removeEventListener("beforeinstallprompt", handlePrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const instructions = useMemo(() => {
    if (device.deviceType === "ios") {
      return {
        title: "Install ManuCalc on iPhone or iPad",
        limitation: "iOS does not allow websites to open the install prompt automatically.",
        steps: ["Tap the Share button in Safari.", "Tap Add to Home Screen.", "Tap Add, then open ManuCalc from the new icon."],
      };
    }

    if (device.deviceType === "android") {
      return {
        title: "Install ManuCalc on Android",
        limitation: promptEvent ? "Your browser can show the native install prompt." : "If the prompt is hidden, use Chrome menu > Install app.",
        steps: ["Tap Download Mobile/Desktop App.", "Confirm Install when Chrome prompts.", "Open ManuCalc from your home screen and sign in."],
      };
    }

    return {
      title: `Install ManuCalc on ${device.browserName}`,
      limitation: promptEvent ? "Your browser can show the native install prompt." : "Use the browser address-bar install icon or menu if the prompt is unavailable.",
      steps: ["Click Download Mobile/Desktop App.", "Confirm the browser install prompt when available.", "Open ManuCalc from your app launcher and sign in."],
    };
  }, [device, promptEvent]);

  const startInstall = useCallback(async () => {
    setInstallResult("");

    if (detectDevice().isStandalone) {
      setDevice(detectDevice());
      setInstallResult("ManuCalc is already installed.");
      setGuideOpen(true);
      return { outcome: "already-installed" };
    }

    if (promptEvent) {
      try {
        promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        setPromptEvent(null);
        const accepted = choice?.outcome === "accepted";
        setInstallResult(
          accepted
            ? "Install started. Open ManuCalc from your app launcher when it finishes."
            : "Install was dismissed. You can try again from the browser install menu."
        );
        setGuideOpen(!accepted);
        return choice;
      } catch (error) {
        setInstallResult("The browser install prompt could not open. Use the guided steps below.");
        setGuideOpen(true);
        return { outcome: "error", error };
      }
    }

    setGuideOpen(true);
    setInstallResult(
      device.deviceType === "ios"
        ? "Follow the iPhone/iPad install steps below."
        : "Native install prompt is not currently available. Use the guided steps below."
    );
    return { outcome: "manual-instructions" };
  }, [device.deviceType, promptEvent]);

  return {
    canInstall: Boolean(promptEvent),
    isInstalled: device.isStandalone,
    deviceType: device.deviceType,
    browserName: device.browserName,
    serviceWorkerReady,
    installResult,
    guideOpen,
    setGuideOpen,
    instructions,
    startInstall,
  };
};

export default usePwaInstall;
